/* eslint-disable react-hooks/exhaustive-deps */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Box,
  TextField,
  useMediaQuery,
  useTheme,
  Typography,
  Container,
  InputAdornment,
  IconButton,
  Button,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useSelector } from "react-redux";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { RootState, useAppDispatch } from "@store/store";
import { TerminosCondiciones } from "@components/TerminosCondiciones";
import {
  PASSWORD,
  HAS_SPECIAL_CHARACTERS,
  HAS_NUMBERS,
  HAS_UPPERCASE,
  HAS_LOWERCASE,
  HAS_WHITESPACE,
} from "@utils/expreciones";
import { getCurrentUser, confirmSignIn } from "aws-amplify/auth";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { ApiError } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import { useLazyGetSessionInfoGuaranteeQuery } from "@api/second_guarantee_api/sessionInfoGuarantee";
import { setStatusConfigSecondGuarantee, setTaxSystemSecondGuarantee, setUserSecondGuaranteeInfo } from "@store/slices/segundoAvalSlice";
import { PODUCT_ID_SECOND_GUARANTEE, TAX_SYSTEM_TYPE_SECONDGUARANTEE, TAX_SYTEM_ID_SECOND_GUARANTE } from "@helpers/constantes";
import { useLazyGetStatusSecondGuaranteeQuery } from "@api/second_guarantee_api/catalogsSecondGuarantee";

export const CrearCuentaSegundoAval = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const interfaceConfig = useInterfaceConfig();
  const guarantee = useSelector((state: RootState) => state.secondGuarante.guarantee);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [triggerGetSessionInfo] = useLazyGetSessionInfoGuaranteeQuery();
  const [triggerGetStatusList] = useLazyGetStatusSecondGuaranteeQuery();

  const formik = useFormik({
    initialValues: {
      password: accountUser.password || "",
    },
    validationSchema: yup.object().shape({
      password: yup.string().required("Campo requerido").matches(PASSWORD, "Formato Incorrecto"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);

        await confirmSignIn({
          challengeResponse: values.password,
        });

        const currentUser = await getCurrentUser();

        if (!currentUser?.userId) {
          console.warn("No se encontró userId en currentUser");
          return;
        }

        const sessionInfo = await triggerGetSessionInfo({ id: currentUser.userId }).unwrap();

        const statusResponse = await triggerGetStatusList({
          product_id: PODUCT_ID_SECOND_GUARANTEE,
          tax_system_id: TAX_SYTEM_ID_SECOND_GUARANTE,
        }).unwrap();

        dispatch(setStatusConfigSecondGuarantee(statusResponse));

        dispatch(
          setUserSecondGuaranteeInfo({
            id: sessionInfo.user_endorsement_data.person_id,
            loan_application_id: sessionInfo.loan_application_data.id,
            client_details_id: sessionInfo.loan_application_data.client_details_id,
            user_id: sessionInfo.user_endorsement_data.id,
          }),
        );
        dispatch(setTaxSystemSecondGuarantee({ tax_system_type: TAX_SYSTEM_TYPE_SECONDGUARANTEE.PERSON.code }));

        navigate("/avales/registro_aval");
      } catch (error) {
        const api_error = error as ApiError;

        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const isLengthValid = formik.values.password.length >= 8 && formik.values.password.length <= 20;
  const hasNumber = HAS_NUMBERS.test(formik.values.password);
  const hasSpecialChar = HAS_SPECIAL_CHARACTERS.test(formik.values.password);
  const hasUpperCase = HAS_UPPERCASE.test(formik.values.password);
  const hasLowerCase = HAS_LOWERCASE.test(formik.values.password);
  const hasWhitespace = HAS_WHITESPACE.test(formik.values.password);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Grid container height={"100%"}>
      <Grid item xs={12} md={8} mt={4}>
        <Box
          sx={{
            mt: isMobileOrTablet ? 1 : 2,
            mx: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant={isMobileOrTablet ? "h4" : "h3"}
            fontWeight={theme?.palette?.fontWeights?.bold}
            textAlign="center"
            color={theme?.palette?.primary?.dark}
          >
            {interfaceConfig.textos_tofu?.pm?.crear_cuenta?.titulo || "Te damos la bienvenida"}
          </Typography>

          <Typography
            mt={isMobileOrTablet ? 2 : 3}
            variant={isMobileOrTablet ? "body2" : "body1"}
            color={theme?.palette?.primary?.main}
          >
            Genera una nueva contraseña para tu cuenta
          </Typography>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
            <form onSubmit={formik.handleSubmit} noValidate>
              <Typography variant={"body1"} color={theme?.palette?.primary?.main} textAlign={"center"} fontWeight={600}>
                {guarantee.email}
              </Typography>

              <Box>
                <TextField
                  id="password"
                  name="password"
                  label="Contraseña"
                  type={showPassword ? "text" : "password"}
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.password}
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  helperText={formik.touched.password && formik.errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                          sx={{ color: theme?.palette?.primary?.main }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  disabled={formik.isSubmitting}
                />
              </Box>
              <Box mt={isMobileOrTablet ? 2 : 3}>
                <Typography
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  fontWeight={500}
                  color={theme?.palette?.primary?.dark}
                >
                  Tu contraseña debe contener:
                </Typography>

                <Typography
                  mt={1}
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  color={isLengthValid ? "green" : "red"}
                  display="flex"
                  alignItems="center"
                >
                  {isLengthValid ? <CheckIcon /> : <CloseIcon />} &nbsp;Entre 8 y 20 caracteres
                </Typography>

                <Typography
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  color={hasNumber ? "green" : "red"}
                  display="flex"
                  alignItems="center"
                >
                  {hasNumber ? <CheckIcon /> : <CloseIcon />} &nbsp;Un carácter numérico
                </Typography>

                <Typography
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  color={hasSpecialChar ? "green" : "red"}
                  display="flex"
                  alignItems="center"
                >
                  {hasSpecialChar ? <CheckIcon /> : <CloseIcon />} &nbsp;Un carácter especial
                </Typography>

                <Typography
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  color={hasUpperCase ? "green" : "red"}
                  display="flex"
                  alignItems="center"
                >
                  {hasUpperCase ? <CheckIcon /> : <CloseIcon />} &nbsp;Una mayúscula
                </Typography>

                <Typography
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  color={hasLowerCase ? "green" : "red"}
                  display="flex"
                  alignItems="center"
                >
                  {hasLowerCase ? <CheckIcon /> : <CloseIcon />} &nbsp;Una minúscula
                </Typography>

                <Typography
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  color={!hasWhitespace ? "green" : "red"}
                  display="flex"
                  alignItems="center"
                >
                  {!hasWhitespace ? <CheckIcon /> : <CloseIcon />} &nbsp;Sin espacios en blanco
                </Typography>
              </Box>

              <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 3} mb={2}>
                <Button
                  variant="default"
                  type="submit"
                  color="primary"
                  disabled={!formik.isValid || formik.isSubmitting}
                >
                  Continuar
                </Button>
              </Box>
            </form>
            <Box sx={{ mt: 4, mb: 2 }}>
              <TerminosCondiciones />
            </Box>
          </Container>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};
